/**
 * `BarsV` — spec §F2 `BarsV(labels, values, {colors, h, unit})`: "value labels above bars".
 *
 * The mockup's `barsV()`: a 290×120 box, 18px of label gutter at the bottom, bars ≤30px
 * wide on an even pitch, the value printed above each bar and the category beneath it, and
 * a 15 % headroom over the tallest bar so that top label is never clipped. No axis is drawn
 * — §F2 gives this chart four categories and a printed number per bar, which is one axis'
 * worth of information already (§G8: "one axis per chart").
 *
 * What real data adds to the mockup's version:
 *
 * 1. **Missing values draw no bar.** §D3 omits units with no data (never zero), so the
 *    caller passes `null` and gets an em dash over an empty slot — a zero-height bar would
 *    claim the country reported nothing *because it achieved nothing*.
 * 2. **A zero/negative maximum is survivable.** `v / 0` is the difference between an empty
 *    chart and a chart of `NaN` path data.
 * 3. **Colours are per bar, positionally supplied by the caller** — which is how §G8's
 *    fixed country palette reaches it (`lib/palette.ts`), rather than this chart knowing
 *    what a country is.
 * 4. **Hover through the shared `ChartTip` portal** (§F2), not the mockup's `data-tip` pass.
 *
 * Both printed numbers are ink tokens, never the bar colour (§F2/§G8).
 */
import { useMemo, useRef } from 'react';

import { NO_VALUE, fmtNum } from '../../lib/format';
import { AxisLabels } from './AxisLabels';
import { useAxisTitles } from './axisContext';
import { hideChartTip, showChartTip } from './chartTipStore';
import { useChartWidth, useChartZoom } from './useChartWidth';
import { LABEL_LINE_H, VALUE_GAP, VALUE_HALO, niceCeil, valueLabel } from './labels';
import { RowLabel } from './RowLabel';
import { axisBands } from './scale';
import { chartFont, wrapText } from '../../lib/text';

/** Font sizes of a bar's printed value and of its category name under it. */
const VALUE_SIZE = 9.5;
const LABEL_SIZE = 9;

/** Mockup geometry — the label gutter, the top padding and the widest a bar may get. */
export const BARSV_PAD_BOTTOM = 18;
export const BARSV_PAD_TOP = 8;
export const BARSV_MAX_BAR_WIDTH = 30;
/**
 * The mockup's `max * 1.15`, kept as the most headroom the chart ever takes. Since the launch
 * round the headroom is measured from the value label's height, which is never more.
 */
export const BARSV_HEADROOM = 1.15;

export interface BarsVProps {
  labels: readonly string[];
  /** One per label; `null`/`undefined`/non-finite = not reported (no bar, em-dash label). */
  values: readonly (number | null | undefined)[];
  /** One per bar, positionally (§G8 fixed categorical order). Defaults to the brand green. */
  colors?: readonly string[];
  w?: number;
  h?: number;
  /** Appended to the tooltip value (`1,204 ha`). */
  unit?: string;
  /** §G3 decimals, for both the printed label and the tooltip. */
  fmt?: number;
  ariaLabel?: string;
  className?: string;
  /** The categories' title ("Country"), centred under the bar labels. */
  xLabel?: string;
  /** The values' title, "quantity (unit)", rotated at the left. */
  yLabel?: string;
}

const DEFAULT_COLOR = '#2E7D32';

export function BarsV({
  labels,
  values,
  colors,
  w: wProp = 290,
  h = 120,
  unit = '',
  fmt = 0,
  ariaLabel,
  className,
  xLabel: xLabelProp,
  yLabel: yLabelProp,
}: BarsVProps) {
  /*
    #749: the chart measures its own box and lays out at that width in real pixels (its
    text stays the size it was designed at); `w` is the width where nothing can measure,
    and the lightbox's zoom (`ChartZoomContext`) is divided out, as `chartW` did.
  */
  const svgRef = useRef<SVGSVGElement>(null);
  const zoom = useChartZoom();
  const outerW = Math.max(120, useChartWidth(svgRef, wProp, { zoom }));
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  // The titles' bands come out of the width and are added under the height (see `BarsH`).
  const bands = axisBands(xLabel, yLabel);
  const w = outerW - bands.left;
  const bars = useMemo(() => {
    const n = Math.max(labels.length, 1);
    const numbers = labels.map((_, i) => {
      const v = values[i];
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });
    const peak = Math.max(0, ...numbers.map((v) => v ?? 0));
    const pitch = (w - 10) / n;
    const barWidth = Math.max(4, Math.min(BARSV_MAX_BAR_WIDTH, pitch - 8));
    // A category name wraps onto a second line under its bar rather than being cut, and the
    // plot gives up the height that line takes (launch round, 7 Oct 2026).
    const font = chartFont(LABEL_SIZE);
    const lines = labels.map((label) => wrapText(label, Math.max(12, pitch - 4), font, 2));
    const extra = (Math.max(1, ...lines.map((l) => l.length)) - 1) * LABEL_SIZE * LABEL_LINE_H;
    const padBottom = BARSV_PAD_BOTTOM + extra;
    const plot = Math.max(10, h - BARSV_PAD_TOP - padBottom);
    // Headroom for the printed value above the tallest bar, measured from its glyph height
    // rather than a flat 15 %, so the bar is no shorter than the figure needs.
    const above = VALUE_GAP + VALUE_SIZE * 0.75 - BARSV_PAD_TOP;
    const max = peak > 0 ? niceCeil(Math.max(peak, (peak * plot) / Math.max(1, plot - Math.max(0, above)))) : 1;

    return labels.map((label, i) => {
      const v = numbers[i] ?? null;
      const height = v === null ? 0 : Math.max(0, (v / max) * plot);
      const x = 6 + i * pitch;
      return { label, lines: lines[i]!, v, x, barWidth, height, y: h - padBottom - height, i, padBottom };
    });
  }, [labels, values, w, h]);

  const valueText = (v: number | null) =>
    v === null ? NO_VALUE : `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  return (
    <svg
      ref={svgRef}
      data-fluid=""
      className={`chart barsv${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${outerW} ${h + bands.bottom}`}
      role="img"
      aria-label={ariaLabel ?? `${labels.length} categories`}
      data-bars={bars.filter((b) => b.v !== null).length}
      onMouseLeave={hideChartTip}
    >
      <AxisLabels
        xLabel={xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + 6}
        plotRight={bands.left + w - 4}
        plotTop={BARSV_PAD_TOP}
        plotBottom={h - (bars[0]?.padBottom ?? BARSV_PAD_BOTTOM)}
        height={h + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined}>
      {bars.map((b) => (
        <g key={b.label} data-barsv-bar={b.i} data-label={b.label}>
          {b.v === null ? null : (
            <rect
              x={b.x}
              y={b.y}
              width={b.barWidth}
              height={b.height}
              rx={3.5}
              fill={colors?.[b.i] ?? DEFAULT_COLOR}
              style={{ cursor: 'pointer' }}
              data-value={valueText(b.v)}
              onMouseEnter={(e) => showChartTip(e, b.label, valueText(b.v))}
              onMouseMove={(e) => showChartTip(e, b.label, valueText(b.v))}
              onMouseLeave={hideChartTip}
            />
          )}
          {/* Above the bar's end, never inside it, on the panel's halo. */}
          <text
            x={b.x + b.barWidth / 2}
            y={b.y - VALUE_GAP}
            textAnchor="middle"
            fontSize={VALUE_SIZE}
            fontWeight={700}
            fontFamily="var(--mono)"
            fill="#22301F"
            style={VALUE_HALO}
            data-barsv-value={b.i}
          >
            {b.v === null ? NO_VALUE : valueLabel(b.v, fmt, unit)}
          </text>
          <RowLabel
            lines={b.lines}
            x={b.x + b.barWidth / 2}
            cy={h - b.padBottom + 4 + (b.lines.length * LABEL_SIZE * LABEL_LINE_H) / 2}
            size={LABEL_SIZE}
            textAnchor="middle"
            fill="#8B958A"
            data-barsv-label={b.i}
          />
        </g>
      ))}
      </g>
    </svg>
  );
}

export default BarsV;
